import type { CameraPresetId } from '../types'

let canvasEl: HTMLCanvasElement | null = null

export function setRecordingCanvas(el: HTMLCanvasElement | null) {
  canvasEl = el
}

export function getRecordingCanvas() {
  return canvasEl
}

export type RecordQuality = '720p' | '1080p'
export type RecordMode = 'tour' | 'single'

export interface RecordOptions {
  quality: RecordQuality
  mode: RecordMode
  durationSec: number
  onProgress: (p: number) => void
  setCameraPreset: (id: CameraPresetId) => void
  getPoseTitle: () => string
}

function pickMime(): { mime: string; ext: string; label: string } {
  const candidates = [
    { mime: 'video/webm;codecs=vp9,opus', ext: 'webm', label: 'WebM VP9' },
    { mime: 'video/webm;codecs=vp8,opus', ext: 'webm', label: 'WebM VP8' },
    { mime: 'video/webm', ext: 'webm', label: 'WebM' },
    { mime: 'video/mp4;codecs=h264', ext: 'mp4', label: 'MP4 H264' },
    { mime: 'video/mp4', ext: 'mp4', label: 'MP4' },
  ]
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c.mime)) return c
  }
  return { mime: '', ext: 'webm', label: 'unsupported' }
}

export function getRecorderSupport(): { ok: boolean; mime: string; label: string; ext: string } {
  if (typeof MediaRecorder === 'undefined' || !HTMLCanvasElement.prototype.captureStream) {
    return { ok: false, mime: '', label: 'MediaRecorder / captureStream not supported', ext: '' }
  }
  const m = pickMime()
  return { ok: !!m.mime, mime: m.mime, label: m.label || 'unsupported', ext: m.ext }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function downloadStillPng(filename = 'yoga-anatomy-studio-still.png') {
  const canvas = getRecordingCanvas()
  if (!canvas) throw new Error('Viewport canvas not ready')
  canvas.toBlob((blob) => {
    if (!blob) throw new Error('Failed to capture PNG')
    downloadBlob(blob, filename)
  }, 'image/png')
}

export interface ActiveRecording {
  stop: () => void
}

export function startRecording(opts: RecordOptions): ActiveRecording {
  const canvas = getRecordingCanvas()
  if (!canvas) throw new Error('Viewport canvas not ready')
  const support = getRecorderSupport()
  if (!support.ok) {
    throw new Error(`Recording not supported (${support.label}). Try Chrome/Edge/Firefox desktop.`)
  }

  const height = opts.quality === '1080p' ? 1080 : 720
  const width = opts.quality === '1080p' ? 1920 : 1280
  const compose = document.createElement('canvas')
  compose.width = width
  compose.height = height
  const ctx = compose.getContext('2d')
  if (!ctx) throw new Error('2D context unavailable')

  const stream = compose.captureStream(30)
  const recorder = new MediaRecorder(stream, {
    mimeType: support.mime,
    videoBitsPerSecond: opts.quality === '1080p' ? 8_000_000 : 4_000_000,
  })
  const chunks: Blob[] = []
  recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data) }

  const tourOrder: CameraPresetId[] =
    opts.mode === 'tour' ? ['front', 'back', 'side', 'underneath'] : []
  const segment = opts.durationSec / Math.max(tourOrder.length, 1)
  let lastPreset: CameraPresetId | null = null
  const started = performance.now()
  let raf = 0
  let stopped = false

  let resolveBlob!: (b: Blob) => void
  const blobPromise = new Promise<Blob>((res) => { resolveBlob = res })

  const finish = () => {
    if (stopped) return
    stopped = true
    cancelAnimationFrame(raf)
    if (recorder.state !== 'inactive') recorder.stop()
  }

  const draw = () => {
    if (stopped) return
    const elapsed = (performance.now() - started) / 1000
    const p = Math.min(1, elapsed / opts.durationSec)
    opts.onProgress(p)

    if (opts.mode === 'tour' && tourOrder.length) {
      const idx = Math.min(tourOrder.length - 1, Math.floor(elapsed / segment))
      const next = tourOrder[idx]
      if (next !== lastPreset) {
        lastPreset = next
        opts.setCameraPreset(next)
      }
    }

    ctx.fillStyle = '#0d2a2a'
    ctx.fillRect(0, 0, width, height)
    const scale = Math.min(width / canvas.width, height / canvas.height)
    const dw = canvas.width * scale
    const dh = canvas.height * scale
    ctx.drawImage(canvas, (width - dw) / 2, (height - dh) / 2, dw, dh)

    const title = opts.getPoseTitle()
    ctx.fillStyle = 'rgba(13, 79, 79, 0.75)'
    ctx.fillRect(24, 24, Math.min(width * 0.6, 640), 64)
    ctx.fillStyle = '#f4f7f7'
    ctx.font = '600 22px system-ui, sans-serif'
    ctx.fillText('Yoga Anatomy Studio', 40, 50)
    ctx.font = '16px system-ui, sans-serif'
    ctx.fillText(title.slice(0, 60), 40, 74)

    ctx.fillStyle = 'rgba(0,0,0,0.55)'
    ctx.fillRect(24, height - 52, width - 48, 32)
    ctx.fillStyle = '#e8f4f4'
    ctx.font = '12px system-ui, sans-serif'
    ctx.fillText(
      'Educational approximation · CC0 Vitruvian / CharMorph body · App MIT · Not medical advice',
      36,
      height - 32,
    )

    ctx.fillStyle = '#1a5c5c'
    ctx.fillRect(0, height - 6, width, 6)
    ctx.fillStyle = '#7ec8c8'
    ctx.fillRect(0, height - 6, width * p, 6)

    if (elapsed >= opts.durationSec) { finish(); return }
    raf = requestAnimationFrame(draw)
  }

  recorder.onstop = () => resolveBlob(new Blob(chunks, { type: support.mime }))
  recorder.start(200)
  raf = requestAnimationFrame(draw)

  blobPromise.then((blob) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-')
    downloadBlob(blob, `yoga-anatomy-${opts.mode}-${opts.quality}-${stamp}.${support.ext}`)
  })

  return { stop: finish }
}
